import React, { useState } from "react";
import { Card } from "components/ui";
import clsx from "clsx";
import { formatBytes } from "utils/formatBytes";
import {
  getFileIcon,
  getFileTypeLabel,
  formatDate,
  getUploaderName,
  getModifierName,
  downloadDocumentWithToast,
  getVerificationStatusInfo,
} from "../utils/documentUtils";
import { DocumentActions } from "./DocumentActions";
import { toast } from "sonner";
import ViewDocumentModal from "./ViewDocumentModal";
import { triggerRowContextMenu } from "custom-components/shared/TableActionMenu";
import { getLeadDocumentPreviewUrl } from "services/leadsService";
import { getJobDocumentPreviewUrl } from "services/jobsService";
import { getAttachmentPreviewUrl } from "services/documentUploadService";
import { DOCUMENT_ENTITY_TYPES } from "services/documentFolderService";

const DocumentCard = ({
  document,
  refetch,
  refreshProgressJourney = () => { },
  onVerificationAction,
  onRename,
  onMove,
  IsFinalApproved = false,
  isStudent = false,
  readOnly = false,
  displayMode = "list",
  entityType = null,
  isSelected = false,
  onToggleSelect = null,
}) => {
  const { Icon, colorClass } = getFileIcon(document.fileType);

  // Safe formatting functions
  const safeFormatBytes = (bytes) => {
    try {
      if (typeof bytes !== "number" || isNaN(bytes) || bytes < 0) {
        return "-";
      }
      return formatBytes(bytes);
    } catch (error) {
      console.warn("Error formatting bytes:", error);
      return "-";
    }
  };

  const safeFormatDate = (dateString) => {
    try {
      if (!dateString) return "-";
      return formatDate(dateString);
    } catch (error) {
      console.warn("Error formatting date:", error);
      return "-";
    }
  };

  const uploadedDate = safeFormatDate(document.UploadedOn);
  const modifiedRaw = document.ModifiedOn;
  const modifiedDate = modifiedRaw ? safeFormatDate(modifiedRaw) : "-";
  const uploaderName = document.UploadedByName || getUploaderName(document);
  const modifierName = document.ModifiedByName || getModifierName(document);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const handleViewDocument = () => {
    setIsViewModalOpen(true);
  };

  const handleDownloadDocument = async (doc) => {
    // Which endpoint resolves this document depends on entityType, not the file's original
    // storage path — see the same reasoning in handleOpenInNewTab below.
    return downloadDocumentWithToast(doc || document, { entityType });
  };

  const handleOpenInNewTab = async (doc) => {
    const targetDoc = doc || document;
    if (!targetDoc) return;

    const docId = targetDoc?.id || targetDoc?.DocumentId;
    const filePathKey = targetDoc?.FilePath || targetDoc?.filePath || targetDoc?.FileKey || targetDoc?.fileKey || targetDoc?.url || targetDoc?.Url;

    if (filePathKey && typeof filePathKey === 'string' && (filePathKey.startsWith('http://') || filePathKey.startsWith('https://'))) {
      window.open(filePathKey, "_blank");
      return;
    }

    // Which endpoint resolves this document depends on which entity's row we're looking at
    // (entityType), not on the file's original storage path - a job's "Sales" folder document is
    // its own JobId-only row that just reuses the S3 key from whichever lead it was copied from,
    // so a lead-documents/ prefix here does NOT mean the row still has a LeadId.
    const isJobDoc = entityType === DOCUMENT_ENTITY_TYPES.JOB;
    const fetchPreviewUrl = isJobDoc ? getJobDocumentPreviewUrl : getLeadDocumentPreviewUrl;

    try {
      let url = "";
      if (docId && !targetDoc.isAdditionalDocument) {
        const result = await fetchPreviewUrl(docId);
        if (result.success && result.data?.url) {
          url = result.data.url;
        }
      }

      if (!url && filePathKey) {
        const result = await getAttachmentPreviewUrl(filePathKey);
        if (result.success && result.url) {
          url = result.url;
        }
      }

      if (url) {
        window.open(url, "_blank");
      } else {
        toast.error("Failed to open document preview URL");
      }
    } catch (err) {
      console.error("Error opening document in new tab:", err);
      toast.error("An error occurred while opening document");
    }
  };

  if (displayMode === "grid") {
    const verificationStatusValue = document?.verificationStatus ?? document?.VerificationStatus;
    const hasVerificationStatus = verificationStatusValue !== undefined && verificationStatusValue !== null;
    const verificationInfo = hasVerificationStatus ? getVerificationStatusInfo(verificationStatusValue) : null;

    const statusBadgeStyles = {
      0: "bg-amber-50 text-amber-700 border-amber-200/80 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800/40",
      1: "bg-primary-50 text-primary-700 border-primary-200/80 dark:bg-primary-950/30 dark:text-primary-400 dark:border-primary-800/40",
      2: "bg-rose-50 text-rose-700 border-rose-200/80 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-800/40",
    };

    return (
      <>
        <Card
          className={clsx(
            "p-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-primary-400 dark:hover:border-primary-500 border group flex flex-col justify-between min-h-[115px] w-full relative rounded-lg overflow-hidden",
            isSelected
              ? "border-primary-500 bg-primary-50/50 dark:bg-primary-900/20 dark:border-primary-500 shadow-xs"
              : "border-gray-200/80 dark:border-dark-600 bg-white dark:bg-dark-800 shadow-2xs"
          )}
          onClick={handleViewDocument}
          onContextMenu={(e) => {
            e.stopPropagation();
            triggerRowContextMenu(e, document);
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              {onToggleSelect && (
                <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={Boolean(isSelected)}
                    onChange={() => onToggleSelect(document)}
                    className="h-4 w-4 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  />
                </div>
              )}
              <div className={clsx("p-1.5 rounded-md bg-primary-50 dark:bg-primary-900/20 border border-primary-100/80 dark:border-primary-800/30 shrink-0 group-hover:scale-105 transition-transform", colorClass)}>
                <Icon className="h-6 w-6" />
              </div>
            </div>

            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              {verificationInfo && (
                <span
                  className={clsx(
                    "px-1.5 py-0.5 rounded text-[10px] font-medium border shrink-0",
                    statusBadgeStyles[verificationInfo.value] || statusBadgeStyles[0]
                  )}
                >
                  {verificationInfo.label}
                </span>
              )}
              <DocumentActions
                document={document}
                entityType={entityType}
                refetch={refetch}
                useCase="card"
                IsFinalApproved={IsFinalApproved}
                refreshProgressJourney={refreshProgressJourney}
                onVerificationAction={onVerificationAction}
                onView={handleViewDocument}
                onRename={onRename}
                onMove={onMove}
                isStudent={isStudent}
                readOnly={readOnly}
              />
            </div>
          </div>

          <div className="min-w-0 mt-2">
            <h3
              className="text-xs font-semibold text-gray-800 dark:text-dark-100 line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
              title={document.name}
            >
              {document.name}
            </h3>

            <div className="flex items-center justify-between text-[10px] text-gray-400 dark:text-dark-400 mt-1 gap-1">
              <span className="truncate bg-gray-50 dark:bg-dark-700/50 px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-500 dark:text-dark-300">
                {getFileTypeLabel(document.fileType)}
              </span>
              <span className="shrink-0 font-medium text-gray-500 dark:text-dark-300 text-[10px]">
                {safeFormatBytes(document.size)}
              </span>
            </div>

            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-dark-700/60 space-y-0.5 text-[10px] text-gray-500 dark:text-dark-400">
              <div className="flex items-center justify-between gap-1">
                <span className="text-gray-400 dark:text-dark-400 font-normal shrink-0">Uploaded:</span>
                <span className="font-medium text-gray-600 dark:text-dark-200 text-right truncate">{uploadedDate}</span>
              </div>
              {!document.isAdditionalDocument && uploaderName && uploaderName !== '-' && uploaderName !== 'System' && (
                <div className="flex items-center justify-between gap-1">
                  <span className="text-gray-400 dark:text-dark-400 font-normal shrink-0">Uploaded By:</span>
                  <span className="font-medium text-gray-600 dark:text-dark-200 text-right truncate" title={uploaderName}>
                    {uploaderName}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between gap-1">
                <span className="text-gray-400 dark:text-dark-400 font-normal shrink-0">Modified:</span>
                <span className="font-medium text-gray-600 dark:text-dark-200 text-right truncate">{modifiedDate}</span>
              </div>
              {!document.isAdditionalDocument && modifierName && modifierName !== '-' && (
                <div className="flex items-center justify-between gap-1">
                  <span className="text-gray-400 dark:text-dark-400 font-normal shrink-0">Modified By:</span>
                  <span className="font-medium text-gray-600 dark:text-dark-200 text-right truncate" title={modifierName}>
                    {modifierName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {isViewModalOpen && (
          <ViewDocumentModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            document={document}
            entityType={entityType}
            onDownload={handleDownloadDocument}
            onOpenInNewTab={handleOpenInNewTab}
            onVerificationAction={onVerificationAction}
            isStudent={isStudent}
          />
        )}
      </>
    );
  }

  return (
    <>
      {/* Borderless File Explorer List View Row Style with pixel-aligned columns */}
      <div
        className={clsx(
          "flex items-center justify-between px-2.5 py-1.5 text-xs rounded-md cursor-pointer transition-colors group leading-tight border-b border-gray-100 dark:border-dark-700/40 last:border-0",
          isSelected
            ? "bg-primary-50/70 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100"
            : "hover:bg-primary-50/50 dark:hover:bg-dark-700/60"
        )}
        onClick={handleViewDocument}
        onContextMenu={(e) => {
          e.stopPropagation();
          triggerRowContextMenu(e, document);
        }}
      >
        {/* File Icon & Name */}
        <div className="flex items-center gap-2 min-w-[180px] flex-1 pr-3">
          {onToggleSelect && (
            <div onClick={(e) => e.stopPropagation()} className="shrink-0 flex items-center pr-1">
              <input
                type="checkbox"
                checked={Boolean(isSelected)}
                onChange={() => onToggleSelect(document)}
                className="h-4 w-4 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
            </div>
          )}
          <div className={clsx("shrink-0 group-hover:scale-105 transition-transform", colorClass)}>
            <Icon className="h-4 w-4" />
          </div>
          <span
            className="text-xs font-medium text-gray-800 dark:text-dark-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
            title={document.name}
          >
            {document.name}
          </span>
        </div>

        {/* Date Uploaded Column */}
        <div className="w-40 shrink-0 pr-2 text-gray-500 dark:text-dark-300 text-xs truncate whitespace-nowrap">
          <div className="font-medium text-gray-700 dark:text-dark-200 truncate">
            {uploadedDate}
          </div>
          {!document.isAdditionalDocument && (
            <div className="text-[10px] text-gray-400 dark:text-dark-400 truncate" title={uploaderName && uploaderName !== '-' ? uploaderName : "System"}>
              by {uploaderName && uploaderName !== '-' ? uploaderName : "System"}
            </div>
          )}
        </div>

        {/* Date Modified Column */}
        <div className="w-40 shrink-0 pr-2 text-gray-500 dark:text-dark-300 text-xs truncate whitespace-nowrap">
          <div className="font-medium text-gray-700 dark:text-dark-200 truncate">
            {modifiedDate}
          </div>
          {!document.isAdditionalDocument && modifierName !== '-' && (
            <div className="text-[10px] text-gray-400 dark:text-dark-400 truncate" title={modifierName}>
              by {modifierName}
            </div>
          )}
        </div>

        {/* Type Column */}
        <div className="w-28 shrink-0 pr-2 text-gray-400 dark:text-dark-400 text-xs truncate whitespace-nowrap">
          {getFileTypeLabel(document.fileType)}
        </div>

        {/* Size Column */}
        <div className="w-20 shrink-0 pr-2 text-gray-500 dark:text-dark-300 text-xs truncate whitespace-nowrap">
          {safeFormatBytes(document.size)}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 shrink-0 w-12 justify-end" onClick={(e) => e.stopPropagation()}>
          <DocumentActions
            document={document}
            entityType={entityType}
            refetch={refetch}
            useCase="card"
            IsFinalApproved={IsFinalApproved}
            refreshProgressJourney={refreshProgressJourney}
            onVerificationAction={onVerificationAction}
            onView={handleViewDocument}
            onRename={onRename}
            onMove={onMove}
            isStudent={isStudent}
            readOnly={readOnly}
          />
        </div>
      </div>

      {isViewModalOpen && (
        <ViewDocumentModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          document={document}
          entityType={entityType}
          onDownload={handleDownloadDocument}
          onOpenInNewTab={handleOpenInNewTab}
          onVerificationAction={onVerificationAction}
          isStudent={isStudent}
        />
      )}
    </>
  );
};

export default DocumentCard;
